import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, getDoc, query, where, doc, setDoc } from 'firebase/firestore';
import { firestore } from '../firebase';
import './SkyScoreAdmin.css';
import { getActiveEventId } from '../utils/activeEvent';
import { getTeamDisplayLabel, getTeamLabelCore, getTeamNumberDisplay } from '../utils/teamDisplayLabel';
import { openPrintWindow, getOperationalTablePrintCss } from '../utils/printUtils';
import { copyText, buildTsvTable } from '../utils/clipboardUtils';
import AdminCopyMenu from './admin/AdminCopyMenu';
import AdminColumnExportModal from './admin/AdminColumnExportModal';

function escapeHtmlCell(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const SKY_SCORE_EXPORT_COLS = [
  { id: 'teamNumber', label: '#', default: true },
  { id: 'teamName', label: 'Team name', default: true },
  { id: 'mentor', label: 'Mentor', default: true },
  ...[1, 2, 3, 4].flatMap((round) => [
    { id: `r${round}raw`, label: `R${round} raw`, default: true },
    { id: `r${round}busts`, label: `R${round} busts`, default: true },
    { id: `r${round}total`, label: `R${round} total`, default: true },
  ]),
];

const UNASSIGNED = '__unassigned__';

const divisionSortKey = (d) => {
  const lower = String(d).toLowerCase();
  if (lower === 'beginner') return 0;
  if (lower === 'intermediate') return 1;
  return 2;
};

/** Flyer lookup fills mentorName when missing (mentor on team is usually an email string). */
const teamWithResolvedMentorName = (team, getMentorNameFn) => {
  const mentorEmail =
    typeof team.mentor === 'string'
      ? team.mentor
      : typeof team.mentor === 'object' && team.mentor != null
        ? team.mentor.email
        : '';
  const fromFlyer = mentorEmail ? getMentorNameFn(mentorEmail) : '';
  return {
    ...team,
    mentorName: team.mentorName || fromFlyer || undefined,
  };
};

const SkyScoreAdmin = () => {
  const [teams, setTeams] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [scores, setScores] = useState({});
  const [flyers, setFlyers] = useState([]);
  const [divisionFilter, setDivisionFilter] = useState('');
  const [skyTableExport, setSkyTableExport] = useState(null); // null | { mode: 'print'|'copy' }
  const [skyExportCols, setSkyExportCols] = useState(() => new Set());
  const [saveStatus, setSaveStatus] = useState('');
  /** Latest row map for save-on-blur (avoids stale `scores` vs last onChange in the same tick). */
  const scoresRef = useRef({});
  const saveStatusTimerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      const id = await getActiveEventId();
      if (!id) return;
      const eventSnap = await getDoc(doc(firestore, 'events', id));
      if (!eventSnap.exists()) return;
      const event = { id: eventSnap.id, ...eventSnap.data() };
      setActiveEvent(event);

      const teamQuery = query(collection(firestore, 'teams'), where('eventId', '==', event.id));
      const teamSnap = await getDocs(teamQuery);
      const teamData = teamSnap.docs.map((d) => ({ ...d.data(), id: d.id }));
      teamData.sort((a, b) => {
        const na = Number(a.teamNumber ?? a.number) || 0;
        const nb = Number(b.teamNumber ?? b.number) || 0;
        return na - nb;
      });
      setTeams(teamData);

      const flyerQuery = query(collection(firestore, 'flyers'), where('eventId', '==', event.id));
      const flyerSnap = await getDocs(flyerQuery);
      setFlyers(flyerSnap.docs.map(doc => doc.data()));

      const scoreQuery = query(
        collection(firestore, 'scores'),
        where('eventId', '==', event.id),
        where('type', '==', 'sky')
      );
      const scoreSnap = await getDocs(scoreQuery);
      const scoresMap = {};
      scoreSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        const key = `${data.teamId}_${data.round}`;
        scoresMap[key] = { raw: data.raw || 0, busts: data.busts || 0 };
      });
      setScores(scoresMap);
      scoresRef.current = scoresMap;
    };

    fetchData();
  }, []);

  useEffect(() => {
    scoresRef.current = scores;
  }, [scores]);

  useEffect(() => {
    return () => {
      if (saveStatusTimerRef.current) {
        clearTimeout(saveStatusTimerRef.current);
        saveStatusTimerRef.current = null;
      }
    };
  }, []);

  const { divisionLabels, hasUnassigned } = useMemo(() => {
    const labels = new Set();
    let unassigned = false;
    teams.forEach((t) => {
      const d = (t.division || '').trim();
      if (d) labels.add(d);
      else unassigned = true;
    });
    const sorted = [...labels].sort((a, b) => {
      const diff = divisionSortKey(a) - divisionSortKey(b);
      return diff !== 0 ? diff : a.localeCompare(b);
    });
    return { divisionLabels: sorted, hasUnassigned: unassigned };
  }, [teams]);

  const filteredTeams = useMemo(() => {
    if (!divisionFilter) return teams;
    if (divisionFilter === UNASSIGNED) {
      return teams.filter((t) => !(t.division || '').trim());
    }
    return teams.filter((t) => (t.division || '').trim() === divisionFilter);
  }, [teams, divisionFilter]);

  const getMentorName = useCallback((email) => {
    const mentor = flyers.find((f) => f.email === email);
    if (!mentor || !mentor.name) return email;
    const [first, ...rest] = mentor.name.split(' ');
    return `${first} ${rest[0]?.charAt(0) || ''}`;
  }, [flyers]);

  const skyExportRows = useMemo(() => {
    return filteredTeams.map((team) => {
      const forLabel = teamWithResolvedMentorName(team, getMentorName);
      const row = {
        teamNumber: getTeamNumberDisplay(team),
        teamName: getTeamLabelCore(forLabel),
        mentor: getMentorName(team.mentor),
      };
      for (let round = 1; round <= 4; round++) {
        const key = `${team.id}_${round}`;
        const raw = scores[key]?.raw ?? 0;
        const busts = scores[key]?.busts ?? 0;
        const rawN = raw === '' ? 0 : Number(raw);
        const bustsN = busts === '' ? 0 : Number(busts);
        const total = Math.max(0, rawN - bustsN);
        row[`r${round}raw`] = String(rawN);
        row[`r${round}busts`] = String(bustsN);
        row[`r${round}total`] = String(total);
      }
      return row;
    });
  }, [filteredTeams, scores, getMentorName]);

  useEffect(() => {
    if (!skyTableExport) return;
    setSkyExportCols(new Set(SKY_SCORE_EXPORT_COLS.filter((c) => c.default).map((c) => c.id)));
  }, [skyTableExport]);

  const openSkyTableExport = (mode) => {
    if (!skyExportRows.length) {
      window.alert('No teams in the current division filter.');
      return;
    }
    setSkyTableExport({ mode });
  };

  const resetSkyExportCols = () => {
    setSkyExportCols(new Set(SKY_SCORE_EXPORT_COLS.filter((c) => c.default).map((c) => c.id)));
  };

  const runSkyTableExport = (selectedColumnIds) => {
    if (!skyTableExport || !activeEvent) return;
    const { mode } = skyTableExport;
    const colSet =
      Array.isArray(selectedColumnIds) && selectedColumnIds.length > 0
        ? new Set(selectedColumnIds)
        : skyExportCols;
    const active = SKY_SCORE_EXPORT_COLS.filter((c) => colSet.has(c.id));
    if (!active.length) return;
    const colMeta = active.map((c) => ({ id: c.id, header: c.label }));
    const rowRecords = skyExportRows.map((r) => {
      const o = {};
      active.forEach((c) => {
        o[c.id] = r[c.id] ?? '';
      });
      return o;
    });
    const evTitle = activeEvent.title || activeEvent.id || 'Sky scores';
    const divLabel = divisionFilter
      ? divisionFilter === UNASSIGNED
        ? 'Unassigned'
        : divisionFilter
      : 'All divisions';
    const sub = `Sky score entry — ${divLabel}`;
    if (mode === 'copy') {
      copyText(buildTsvTable(colMeta, rowRecords));
    } else {
      const headerCells = active.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
      const rowsHtml = rowRecords
        .map((rec) => {
          const cells = active.map((c) => `<td>${escapeHtmlCell(rec[c.id])}</td>`).join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      const bodyHtml = `<main style="padding:8px 12px"><h1>${escapeHtmlCell(evTitle)}</h1><p>${escapeHtmlCell(sub)}</p><p>${rowRecords.length} team(s)</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table></main>`;
      openPrintWindow(`${evTitle} — ${sub}`, bodyHtml, getOperationalTablePrintCss());
    }
    setSkyTableExport(null);
  };

  const normalizeScoreInput = (value) => {
    if (value === '') return '';
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return null;
    if (parsed < 0) return 0;
    if (parsed > 999) return 999;
    return parsed;
  };

  const handleChange = (teamId, round, field, value) => {
    console.log('[SkyScoreAdmin] score-input onChange', {
      teamId,
      skyRound: round,
      field,
      rawValue: value,
      eventId: activeEvent?.id ?? null,
      eventTitle: activeEvent?.title ?? null,
    });
    const key = `${teamId}_${round}`;
    const normalized = normalizeScoreInput(value);
    if (normalized === null) return;
    setScores((prev) => {
      const next = {
        ...prev,
        [key]: {
          ...prev[key],
          [field]: normalized,
        },
      };
      scoresRef.current = next;
      return next;
    });
  };

  const handleSave = (teamId, round, field, rawValue) => {
    if (!activeEvent?.id) return Promise.resolve();
    const key = `${teamId}_${round}`;
    const normalizedFieldValue = normalizeScoreInput(rawValue);
    if (normalizedFieldValue === null) return Promise.resolve();
    const stateData = scoresRef.current[key] || scores[key] || {};
    const nextData = {
      ...stateData,
      [field]: normalizedFieldValue,
    };
    const raw = nextData.raw === '' ? 0 : nextData.raw ?? 0;
    const busts = nextData.busts === '' ? 0 : nextData.busts ?? 0;
    const total = raw - busts;

    const docId = `${teamId}_sky_${round}`;
    const payload = {
      eventId: activeEvent.id,
      teamId,
      type: 'sky',
      round,
      raw,
      busts,
      value: total,
    };

    console.log('[SkyScoreAdmin] save handler: persisting sky score', {
      firestoreCollection: 'scores',
      documentId: docId,
      fullPath: `scores/${docId}`,
      note:
        'Sky scores live in top-level `scores` with doc id `{teamId}_sky_{round}`; edition (e.g. 2027) is determined by `payload.eventId`, not the path.',
      eventId: activeEvent.id,
      teamId,
      skyRound: round,
      fieldEdited: field,
      rawInput: rawValue,
      normalizedFieldValue,
      payload,
    });

    if (saveStatusTimerRef.current) {
      clearTimeout(saveStatusTimerRef.current);
      saveStatusTimerRef.current = null;
    }

    return setDoc(doc(firestore, 'scores', docId), payload)
      .then(() => {
        setSaveStatus('saved');
        saveStatusTimerRef.current = setTimeout(() => {
          setSaveStatus('');
          saveStatusTimerRef.current = null;
        }, 2000);
      })
      .catch((err) => {
        console.error('Score save failed:', err);
        setSaveStatus('error');
        saveStatusTimerRef.current = setTimeout(() => {
          setSaveStatus('');
          saveStatusTimerRef.current = null;
        }, 2500);
      });
  };

  const handleInputKeyDown = (e) => {
    const inputs = Array.from(document.querySelectorAll('.score-input'));
    const idx = inputs.indexOf(e.target);
    if (e.key === 'Tab') {
      e.preventDefault();
      const next = inputs[idx + (e.shiftKey ? -1 : 1)];
      if (next) next.focus();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    }
  };

  const renderInput = (teamId, round, field) => {
    const key = `${teamId}_${round}`;
    return (
      <input
        type="number"
        min="0"
        max="999"
        value={scores[key]?.[field] ?? ''}
        onChange={(e) => handleChange(teamId, round, field, e.target.value)}
        onBlur={(e) => {
          console.log('[SkyScoreAdmin] score-input onBlur → will save', {
            teamId,
            skyRound: round,
            field,
            valueFromInput: e.target.value,
            eventId: activeEvent?.id ?? null,
          });
          void handleSave(teamId, round, field, e.target.value);
        }}
        onKeyDown={handleInputKeyDown}
        className="score-input"
      />
    );
  };

  return (
    <div className="sky-score-admin">
      <h2>Sky Score Entry</h2>
      <div className="sky-score-division-filter">
        <label htmlFor="sky-score-division">Division</label>
        <select
          id="sky-score-division"
          value={divisionFilter}
          onChange={(e) => setDivisionFilter(e.target.value)}
        >
          <option value="">All divisions</option>
          {hasUnassigned && <option value={UNASSIGNED}>Unassigned</option>}
          {divisionLabels.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>
      <div className="sky-score-export-toolbar">
        <button
          type="button"
          className="btn-fm-print-outline"
          onClick={() => openSkyTableExport('print')}
          disabled={!skyExportRows.length}
        >
          Print table
        </button>
        <AdminCopyMenu
          ariaLabel="Copy sky scores table"
          disabled={!skyExportRows.length}
          items={[
            {
              label: 'Copy table…',
              showCopiedFeedback: false,
              onSelect: () => openSkyTableExport('copy'),
            },
          ]}
        />
        {saveStatus === 'saved' && (
          <span className="save-feedback success">Saved ✓</span>
        )}
        {saveStatus === 'error' && (
          <span className="save-feedback error">Save failed</span>
        )}
      </div>
      <table className="sky-score-table">
        <thead>
          <tr>
            <th className="sky-score-team-col">Team</th>
            <th>Mentor</th>
            {[1, 2, 3, 4].map(round => (
              <React.Fragment key={round}>
                <th className="narrow">R{round}</th>
                <th className="narrow">B{round}</th>
                <th className="narrow">T{round}</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredTeams.map(team => (
            <tr key={team.id}>
              <td className="sky-score-team-col sky-score-team-label">
                {getTeamDisplayLabel(teamWithResolvedMentorName(team, getMentorName))}
              </td>
              <td>{getMentorName(team.mentor)}</td>
              {[1, 2, 3, 4].map(round => {
                const key = `${team.id}_${round}`;
                const raw = scores[key]?.raw || 0;
                const busts = scores[key]?.busts || 0;
                const total = Math.max(0, raw - busts);
                return (
                  <React.Fragment key={round}>
                    <td className="narrow">{renderInput(team.id, round, 'raw')}</td>
                    <td className="narrow">{renderInput(team.id, round, 'busts')}</td>
                    <td className="narrow"><strong>{total}</strong></td>
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {skyTableExport && (
        <AdminColumnExportModal
          mode={skyTableExport.mode}
          columns={SKY_SCORE_EXPORT_COLS}
          selectedIds={skyExportCols}
          onChangeSelected={setSkyExportCols}
          onCancel={() => setSkyTableExport(null)}
          onConfirm={runSkyTableExport}
          onResetDefaults={resetSkyExportCols}
        />
      )}
    </div>
  );
};

export default SkyScoreAdmin;
